import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { AttachFile, CloudUpload, Close, Download } from '@mui/icons-material';
import { format } from 'date-fns';
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import AutoSelector from '@/__core/components/AutoSelector';
import { AddButtonSelect } from '@/__core/hocs';
import LabeledField from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { getEmployeeByUser, getEmployees } from '@/modules/employees/redux/EmployeeCRUD';
import {
  deleteTaskAttachment,
  downloadTaskAttachment,
  getTaskAttachmentList,
  uploadTaskAttachmentFile,
} from '@/modules/tasks/redux/TaskCRUD';
import PriorityCreationDialog from './PriorityCreationDialog';
import {
  Task,
  TaskStatus,
  TaskPriority,
  User,
  CreateTaskRequest,
} from '../models/TaskModel';

interface NewTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (taskData: CreateTaskRequest) => Promise<Task | void>;
  taskStatuses: TaskStatus[];
  taskPriorities: TaskPriority[];
  users: User[];
  companyId?: string;
  getUserDisplayName: (user: User) => string;
  orderId?: string;
  // Edit mode props
  editingTask?: Task | null;
  isEditMode?: boolean;
}

const ORDER_TASK_MODEL = 'Order';
const ORDER_TASK_CONTENT_TYPE_ID = 34;

const getAttachmentName = (attachment: any) =>
  String(attachment?.filename || attachment?.name || attachment?.file_name || 'File').trim();

const getAttachmentSize = (attachment: any) =>
  Number(attachment?.file_size || attachment?.size || 0);

const resolveTaskId = (task?: Task | void) => {
  if (!task) return undefined;
  return String((task as any)?.id || (task as any)?.task?.id || '').trim() || undefined;
};

const NewTaskModal: React.FC<NewTaskModalProps> = ({
  open,
  onClose,
  onSubmit,
  taskStatuses,
  taskPriorities,
  users,
  companyId,
  getUserDisplayName,
  orderId,
  editingTask = null,
  isEditMode = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [initialUploadedDocumentIds, setInitialUploadedDocumentIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [employeeUserOptions, setEmployeeUserOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingEmployeeOptions, setLoadingEmployeeOptions] = useState(false);
  const currentUser = useSelector((state: any) => state.auth.user);

  const toDateInputValue = (value?: string | Date | null) => {
    if (!value) return format(new Date(), 'yyyy-MM-dd');
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? format(new Date(), 'yyyy-MM-dd') : format(parsed, 'yyyy-MM-dd');
  };

  const [newTask, setNewTask] = useState({
    title: '',
    content: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(new Date(), 'yyyy-MM-dd'),
    status_id: '',
    priority_id: '',
    assigned_to_user_ids: [] as string[],
    notes: '',
  });
  const [initialTask, setInitialTask] = useState({
    title: '',
    content: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(new Date(), 'yyyy-MM-dd'),
    status_id: '',
    priority_id: '',
    assigned_to_user_ids: [] as string[],
    notes: '',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (isEditMode && editingTask) {
        // Pre-fill form with editing task data
        const assignedEmployees = Array.isArray((editingTask as any)?.assigned_to_employees)
          ? ((editingTask as any).assigned_to_employees as any[])
          : [];
        const assignedUserIdsFromEmployees = assignedEmployees
          .map((employee) => String(employee?.id || '').trim())
          .filter(Boolean);
        const assignedUserIdsFromTask = Array.isArray((editingTask as any)?.assigned_to_user_ids)
          ? ((editingTask as any).assigned_to_user_ids as any[]).map((id) => String(id || '').trim()).filter(Boolean)
          : [];
        const fallbackUserId = typeof editingTask.assigned_to_user === 'string'
          ? editingTask.assigned_to_user
          : editingTask.assigned_to_user?.id || '';
        const assignedUserIds = assignedUserIdsFromEmployees.length
          ? assignedUserIdsFromEmployees
          : assignedUserIdsFromTask.length
            ? assignedUserIdsFromTask
            : (fallbackUserId ? [String(fallbackUserId).trim()] : []);
        
        const prefilledTask = {
          title: editingTask.title || '',
          content: editingTask.content || '',
          start_date: toDateInputValue(editingTask.start_date),
          due_date: toDateInputValue(editingTask.due_date),
          status_id: typeof editingTask.status === 'string' ? editingTask.status : editingTask.status?.id || '',
          priority_id: typeof editingTask.priority === 'string' ? editingTask.priority : editingTask.priority?.id || '',
          assigned_to_user_ids: assignedUserIds,
          notes: editingTask.notes || '',
        };
        setNewTask(prefilledTask);
        setInitialTask(prefilledTask);
      } else {
        // Reset form for new task
        const defaultTask = {
          title: '',
          content: '',
          start_date: format(new Date(), 'yyyy-MM-dd'),
          due_date: format(new Date(), 'yyyy-MM-dd'),
          status_id: '',
          priority_id: '',
          assigned_to_user_ids: [] as string[],
          notes: '',
        };
        setNewTask(defaultTask);
        setInitialTask(defaultTask);
      }

      // Reset attachments state
      setSelectedFiles([]);
      setUploadedDocuments([]);
      setInitialUploadedDocumentIds([]);

      setError(null);
      setSubmitting(false);
    }
  }, [open, isEditMode, editingTask]);

  // Load uploaded attachments in edit mode
  useEffect(() => {
    const loadAttachments = async () => {
      if (!open || !isEditMode || !editingTask?.id) {
        return;
      }

      try {
        setLoadingDocuments(true);
        const attachments = await getTaskAttachmentList(editingTask.id);
        setUploadedDocuments(attachments);
        setInitialUploadedDocumentIds(
          attachments.map((attachment: any) => String(attachment.id)).sort(),
        );
      } catch {
        setUploadedDocuments([]);
        setInitialUploadedDocumentIds([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadAttachments();
  }, [open, isEditMode, editingTask?.id]);

  const hasFormChanges = useMemo(() => {
    const taskChanged = (
      newTask.title !== initialTask.title ||
      newTask.content !== initialTask.content ||
      newTask.start_date !== initialTask.start_date ||
      newTask.due_date !== initialTask.due_date ||
      newTask.status_id !== initialTask.status_id ||
      newTask.priority_id !== initialTask.priority_id ||
      JSON.stringify(newTask.assigned_to_user_ids) !== JSON.stringify(initialTask.assigned_to_user_ids) ||
      newTask.notes !== initialTask.notes
    );

    const uploadedDocumentIds = uploadedDocuments.map((d: any) => String(d.id)).sort();
    const attachmentsChanged =
      selectedFiles.length > 0 ||
      JSON.stringify(uploadedDocumentIds) !== JSON.stringify(initialUploadedDocumentIds);

    return taskChanged || attachmentsChanged;
  }, [initialTask, newTask, uploadedDocuments, initialUploadedDocumentIds, selectedFiles.length]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    // Allow re-selecting the same file
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveUploadedDocument = async (attachmentId: string) => {
    try {
      await deleteTaskAttachment(attachmentId);
      setUploadedDocuments((prev) => prev.filter((doc) => String(doc.id) !== attachmentId));
      setInitialUploadedDocumentIds((prev) => prev.filter((id) => id !== attachmentId));
    } catch {
      setError('Không thể xóa file');
    }
  };

  const handleDownloadAttachment = async (attachment: any) => {
    try {
      await downloadTaskAttachment(
        String(attachment.id),
        getAttachmentName(attachment),
        attachment,
      );
    } catch {
      setError('Không thể tải xuống file');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const uploadSelectedFiles = async (taskId: string, filesToUpload: File[]) => {
    if (!taskId || filesToUpload.length === 0) return;

    setLoadingDocuments(true);
    try {
      await Promise.all(filesToUpload.map((file) => uploadTaskAttachmentFile(taskId, file)));
      const attachments = await getTaskAttachmentList(taskId);
      setUploadedDocuments(attachments);
      setInitialUploadedDocumentIds(attachments.map((attachment: any) => String(attachment.id)).sort());
      setSelectedFiles([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (!open || isEditMode || editingTask) {
      return;
    }

    setNewTask((prev) => {
      if (prev.status_id) {
        return prev;
      }

      const defaultStatus = (taskStatuses || []).find((status: TaskStatus) => status.is_default) || (taskStatuses || [])[0];
      if (!defaultStatus?.id) {
        return prev;
      }

      return {
        ...prev,
        status_id: defaultStatus.id,
      };
    });
  }, [open, isEditMode, editingTask, taskStatuses]);

  useEffect(() => {
    if (!open || isEditMode || editingTask) {
      return;
    }

    if (!newTask.status_id || initialTask.status_id) {
      return;
    }

    // Sync initial snapshot with auto-selected default status
    // so submit remains disabled until user makes a real change.
    setInitialTask((prev) => ({
      ...prev,
      status_id: newTask.status_id,
    }));
  }, [open, isEditMode, editingTask, newTask.status_id, initialTask.status_id]);

  useEffect(() => {
    if (!open || isEditMode || editingTask || !companyId) {
      return;
    }

    if ((newTask.assigned_to_user_ids || []).length > 0) {
      return;
    }

    let isMounted = true;

    const setDefaultAssignee = async () => {
      try {
        if (!currentUser?.id) {
          return;
        }

        const response = await getEmployeeByUser(String(currentUser.id));
        if (!isMounted || response.status !== 200 || !response.data?.id) {
          return;
        }

        setNewTask((prev) => ({
          ...prev,
          assigned_to_user_ids: [String(currentUser.id)],
        }));
      } catch {
        // Current user is not an employee, keep empty default assignee.
      }
    };

    setDefaultAssignee();

    return () => {
      isMounted = false;
    };
  }, [open, isEditMode, editingTask, companyId, currentUser?.id, newTask.assigned_to_user_ids]);

  useEffect(() => {
    if (!open || !companyId) {
      setEmployeeUserOptions([]);
      setLoadingEmployeeOptions(false);
      return;
    }

    let isMounted = true;

    const fetchEmployeeUsers = async () => {
      try {
        setLoadingEmployeeOptions(true);
        const response = await getEmployees({
          company: companyId,
          status: 'PROBATION,WORKING',
          attendance_type: 'attendance',
          no_pagination: true,
        });

        const rawRows = Array.isArray(response.data)
          ? response.data
          : (response.data?.results || []);

        const mapByUserId = new Map<string, { id: string; name: string }>();

        rawRows.forEach((employee: any) => {
          const userId = String(employee?.user?.id || '').trim();
          if (!userId) return;

          const displayName =
            String(employee?.nickname || '').trim() ||
            String(employee?.name || '').trim() ||
            String(employee?.user?.name || '').trim() ||
            String(employee?.user?.email || '').trim() ||
            `User ${userId.slice(0, 8)}`;

          mapByUserId.set(userId, { id: userId, name: displayName });
        });

        if (isMounted) {
          setEmployeeUserOptions(Array.from(mapByUserId.values()));
        }
      } catch {
        if (isMounted) {
          setEmployeeUserOptions([]);
        }
      } finally {
        if (isMounted) {
          setLoadingEmployeeOptions(false);
        }
      }
    };

    fetchEmployeeUsers();

    return () => {
      isMounted = false;
    };
  }, [open, companyId]);

  const assigneeOptions = useMemo(() => {
    const optionsMap = new Map<string, { id: string; name: string }>();

    employeeUserOptions.forEach((option) => {
      optionsMap.set(option.id, option);
    });

    (newTask.assigned_to_user_ids || []).forEach((userId) => {
      if (optionsMap.has(userId)) {
        return;
      }

      const fallbackUser = (users || []).find((user) => user.id === userId);
      optionsMap.set(userId, {
        id: userId,
        name: fallbackUser ? getUserDisplayName(fallbackUser) : `User ${userId.slice(0, 8)}`,
      });
    });

    return Array.from(optionsMap.values());
  }, [employeeUserOptions, getUserDisplayName, newTask.assigned_to_user_ids, users]);

  const handleInputChange = (key: string, value: any) => {
    setNewTask((prev) => {
      const updated = {
        ...prev,
        [key]: value,
      };

      if ((key === 'start_date' || key === 'due_date') && updated.start_date && updated.due_date) {
        if (new Date(updated.due_date) < new Date(updated.start_date)) {
          setError('Ngày hết hạn không thể nhỏ hơn ngày bắt đầu');
        } else {
          setError(null);
        }
      }

      return updated;
    });
  };

  const sections: AppFormSection[] = useMemo(() => ([
    {
      key: 'task-form',
      fields: [
        {
          key: 'title',
          label: 'Tiêu đề',
          type: 'text',
          required: true,
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
        {
          key: 'content',
          label: 'Nội dung',
          type: 'text',
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
        {
          key: 'start_date',
          label: 'Ngày bắt đầu',
          type: 'date',
          required: true,
          gridSize: { xs: 12, md: 6 },
          labelWidth: 120,
        },
        {
          key: 'due_date',
          label: 'Ngày hết hạn',
          type: 'date',
          required: true,
          gridSize: { xs: 12, md: 6 },
          labelWidth: 120,
        },
        {
          key: 'status_id',
          label: '',
          type: 'custom',
          standalone: true,
          gridSize: { xs: 12, md: 6 },
          render: ({ values, onChange }) => (
            <AutoSelector
              value={values.status_id ? [values.status_id] : []}
              onChange={(ids) => onChange('status_id', ids[0] || '')}
              label='Trạng thái'
              labelWidth={120}
              multiple={false}
              placeholder='Chọn trạng thái'
              noOptionsText='Không có trạng thái'
              options={(taskStatuses || []).map((status: TaskStatus) => ({
                id: status.id,
                name: status.name,
                color: status.color,
              }))}
            />
          ),
        },
        {
          key: 'priority_id',
          label: 'Độ ưu tiên',
          type: 'custom',
          gridSize: { xs: 12, md: 6 },
          labelWidth: 120,
          render: ({ values, onChange }) => (
            <AddButtonSelect
              key={`priority-select-${taskPriorities?.length || 0}`}
              options={(taskPriorities || []).map((priority: TaskPriority) => ({ ...priority, name: priority.name }))}
              value={values.priority_id}
              onChange={(event: any) => onChange('priority_id', event.target.value)}
              placeholder='Chọn độ ưu tiên'
              size='small'
              CustomDialog={PriorityCreationDialog}
              onAddNewCustom={async (priorityData: any) => {
                const actualPriority = priorityData.payload || priorityData;
                const priorityId = actualPriority.id;
                onChange('priority_id', priorityId);
                return {
                  id: priorityId,
                  name: actualPriority.name,
                };
              }}
            />
          ),
        },
        {
          key: 'assigned_to_user_ids',
          label: '',
          type: 'custom',
          standalone: true,
          gridSize: { xs: 12 },
          render: ({ values, onChange }) => (
            <AutoSelector
              value={values.assigned_to_user_ids || []}
              onChange={(ids) => onChange('assigned_to_user_ids', ids)}
              label='Gán cho nhân viên'
              labelWidth={120}
              multiple
              loading={loadingEmployeeOptions}
              noOptionsText={companyId ? 'Không có nhân viên phù hợp' : 'Chưa có công ty để tải nhân viên'}
              placeholder={companyId ? 'Chọn nhân viên' : 'Chưa có công ty'}
              options={assigneeOptions}
            />
          ),
        },
        {
          key: 'notes',
          label: 'Ghi chú',
          type: 'text',
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
        {
          key: 'attachments',
          label: '',
          type: 'custom',
          standalone: true,
          gridSize: { xs: 12 },
          render: () => (
            <LabeledField label='File đính kèm' labelWidth={120} alignItems='start'>
              <Box sx={sharedFormStyles.fileUploadContainer}>
                <CloudUpload
                  sx={sharedFormStyles.fileUploadIcon}
                  onClick={() => {
                    if (!submitting) {
                      fileInputRef.current?.click();
                    }
                  }}
                />
                <input
                  type='file'
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  disabled={submitting}
                  style={sharedFormStyles.hiddenFileInput}
                  multiple
                  accept='.txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp'
                />

                {loadingDocuments && (
                  <Box sx={sharedFormStyles.fileLoadingBox}>
                    <Typography variant='body2' color='text.secondary'>
                      Đang tải files...
                    </Typography>
                  </Box>
                )}

                {!loadingDocuments && uploadedDocuments.length > 0 && (
                  <Box sx={sharedFormStyles.fileListBox}>
                    <Typography variant='subtitle2' sx={sharedFormStyles.uploadedFilesTitle}>
                      Files đã upload ({uploadedDocuments.length})
                    </Typography>

                    {uploadedDocuments.map((doc: any) => (
                      <Paper key={String(doc.id)} sx={sharedFormStyles.uploadedFileItem}>
                        <Box sx={sharedFormStyles.fileMetaRow}>
                          <AttachFile sx={sharedFormStyles.successStatusIcon} />
                          <Box>
                            <Typography variant='body2' sx={sharedFormStyles.optionNameText}>
                              {getAttachmentName(doc)}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              {getAttachmentSize(doc) ? formatFileSize(getAttachmentSize(doc)) : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title='Tải xuống'>
                            <IconButton
                              size='small'
                              onClick={() => handleDownloadAttachment(doc)}
                              disabled={submitting}
                              sx={{ color: 'info.main' }}
                            >
                              <Download fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size='small'
                            onClick={() => handleRemoveUploadedDocument(String(doc.id))}
                            disabled={submitting}
                            sx={sharedFormStyles.destructiveIconButton}
                          >
                            <Close />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}

                {selectedFiles.length > 0 && (
                  <Box sx={sharedFormStyles.fileListBox}>
                    <Typography variant='subtitle2' sx={sharedFormStyles.selectedFilesTitle}>
                      Files mới chọn ({selectedFiles.length})
                    </Typography>
                    {selectedFiles.map((file, index) => (
                      <Paper key={`${file.name}-${index}`} sx={sharedFormStyles.selectedFileItem}>
                        <Box sx={sharedFormStyles.fileMetaRow}>
                          <AttachFile sx={sharedFormStyles.primaryStatusIcon} />
                          <Box>
                            <Typography variant='body2' sx={sharedFormStyles.optionNameText}>
                              {file.name}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size='small'
                          onClick={() => handleRemoveFile(index)}
                          disabled={submitting}
                          sx={sharedFormStyles.destructiveIconButton}
                        >
                          <Close />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </LabeledField>
          ),
        },
      ],
    },
  ]), [
    assigneeOptions,
    companyId,
    loadingEmployeeOptions,
    taskPriorities,
    taskStatuses,
    selectedFiles,
    uploadedDocuments,
    loadingDocuments,
    submitting,
  ]);

  const handleCreateTask = async () => {
    // Validate required fields
    if (!newTask.title.trim()) {
      setError('Vui lòng nhập tiêu đề nhiệm vụ');
      return;
    }
    if (!newTask.start_date) {
      setError('Vui lòng chọn ngày bắt đầu');
      return;
    }
    if (!newTask.due_date) {
      setError('Vui lòng chọn ngày hết hạn');
      return;
    }

    if (!orderId) {
      setError('Không tìm thấy đơn hàng hiện tại để gắn nhiệm vụ');
      return;
    }

    const startDate = new Date(newTask.start_date);
    const dueDate = new Date(newTask.due_date);
    
    // Validate date logic
    if (dueDate < startDate) {
      setError('Ngày hết hạn không thể nhỏ hơn ngày bắt đầu');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);

      const filesToUpload = [...selectedFiles];

      const { assigned_to_user_ids, ...restTask } = newTask;
      const normalizedAssignedUserIds = Array.isArray(assigned_to_user_ids)
        ? assigned_to_user_ids.filter(Boolean)
        : [];

      const relatedModelObjects = [{
        model: ORDER_TASK_MODEL,
        content_type_id: ORDER_TASK_CONTENT_TYPE_ID,
        object_id: orderId,
      }];
      
      const taskData: CreateTaskRequest = {
        ...restTask,
        start_date: startDate.toISOString(),
        due_date: dueDate.toISOString(),
        company: companyId || undefined,
        content_type_id: ORDER_TASK_CONTENT_TYPE_ID,
        object_id: orderId,
        related_model_objects: relatedModelObjects,
        assigned_to_user_ids: normalizedAssignedUserIds,
      };

      const result = await onSubmit(taskData);
      const taskIdForUpload = isEditMode && editingTask?.id
        ? editingTask.id
        : resolveTaskId(result);

      if (!isEditMode && filesToUpload.length > 0 && !taskIdForUpload) {
        throw new Error('Không nhận được ID task sau khi tạo');
      }

      if (taskIdForUpload && filesToUpload.length > 0) {
        try {
          await uploadSelectedFiles(taskIdForUpload, filesToUpload);
        } catch {
          setError('Tạo/cập nhật task thành công, nhưng không upload được file');
          return;
        }
      }

      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || (isEditMode ? 'Không thể cập nhật task' : 'Không thể tạo task'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Chỉnh sửa nhiệm vụ' : 'Tạo nhiệm vụ'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <AppForm
          sections={sections}
          values={newTask}
          onChange={handleInputChange}
          onSubmit={handleCreateTask}
          onCancel={onClose}
          submitting={submitting}
          submitDisabled={!isEditMode && !hasFormChanges}
          submitLabel={isEditMode ? 'Cập nhật' : 'Tạo task'}
          submittingLabel={isEditMode ? 'Đang cập nhật...' : 'Đang tạo...'}
          cancelLabel='Hủy'
          containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NewTaskModal;